import { useCallback, useEffect, useState } from 'react';
import { Table, Tooltip } from 'antd';
import { IItemsWithCount, IUserEntity, KeyValueRecord, transformToFormattedTime } from '@rasayi-workspace/shared';
import { DEFAULT_TABLE_STATE } from '@constants';
import { ITableState } from '@interfaces';
import { ColumnsType } from 'antd/es/table';
import { EMPTY_INITIAL_ITEMS, GetTableItems } from '@services';
import TextTypography from 'src/app/components/shared/Typography/Text';
import { TableProps } from 'antd/lib';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { SorterResult } from 'antd/es/table/interface';
import { useLeadContext } from '@contexts';
import { debounce } from 'lodash';
import FilterBar from 'src/app/components/shared/filterBar';

interface ILeadGurantorProps {
    activeTab: string;
}

export const LeadGurators = ({ activeTab }: ILeadGurantorProps) => {
    const { date, leadsGuarantorsFiltars, setLeadsGuarantorsFiltars } = useLeadContext();
    const [leadModalVisible, setLeadModalVisible] = useState<boolean>(false);
    const [leadsGurator, setLeadsGuratorsData] = useState<IItemsWithCount<IUserEntity>>();
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const [tableState, setTableState] = useState<ITableState>({
        ...DEFAULT_TABLE_STATE,
        sortBy: { created_at: 'DESC' },
    });

    const refetch = async (tableStateParams: ITableState, searchParams?: string, qyeryFilter?: string) => {
        setIsFetching(true);
        try {
            const query = `filter=guaranteed_leads.id,nnl,_${searchParams ? `;${searchParams}` : ''}${qyeryFilter || ''}&guarantors=true`;

            const returnedResult: IItemsWithCount<IUserEntity> = await GetTableItems<IUserEntity>(`lead?${query}`, ['product_type', 'channel', 'brand'], [], tableStateParams);
            setLeadsGuratorsData(returnedResult);
        } catch (error) {
            setLeadsGuratorsData(EMPTY_INITIAL_ITEMS);
        } finally {
            setIsFetching(false);
        }
    };

    const handleChange: TableProps<IUserEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<IUserEntity>).columnKey as keyof IUserEntity]: MapAntDesignSortOrder((sorter as SorterResult<IUserEntity>).order),
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters)),
        });
    };

    const columns: ColumnsType<IUserEntity> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[134px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'First name',
            dataIndex: 'first_name',
            key: 'first_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[120px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'Last name',
            dataIndex: 'last_name',
            key: 'last_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[120px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[218px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'Phone Number',
            dataIndex: 'primary_mobile',
            key: 'primary_mobile',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[159px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'Secondary Number',
            dataIndex: 'secondary_mobile',
            key: 'secondary_mobile',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[159px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'CNIC',
            dataIndex: 'cnic_number',
            key: 'cnic_number',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[160px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },

        {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={16} classNames="cursor-pointer flex h-[40px] truncate items-center w-[146px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            ellipsis: true,
            render: (value: string) => (
                <TextTypography fontSize={16} classNames="cursor-pointer flex w-[179px] h-[40px] truncate items-center">
                    {transformToFormattedTime(value)}
                </TextTypography>
            ),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
    ];
    const getData = useCallback(() => {
        return debounce((tableStateParams: ITableState) => {
            let queryParts: string[] = [];

            // Date filters
            if (date?.startDate && date?.endDate) {
                queryParts.push(`created_at,ge,${date.startDate};created_at,le,${date.endDate}`);
            }
            // Join parts and trim extra colons
            let query = queryParts.join('').replace(/:+$/, '');

            let manualilter = '';
            if (leadsGuarantorsFiltars.searchedText) manualilter += '&search_text=${searchText';
            if (date?.startDate && date?.endDate) manualilter += `&startDate=${date.startDate}&endDate=${date.endDate}`;

            refetch(tableStateParams, query, manualilter);
        }, 1000);
    }, [leadsGuarantorsFiltars, date?.startDate, date?.endDate]);

    useEffect(() => {
        if (activeTab === '4') {
            getData()(tableState);
        }
    }, [activeTab, getData, tableState.page, tableState.search, tableState.sortBy]);

    return (
        <>
            <FilterBar
                showSearch={true}
                showFilterButton={true}
                showAddButton={false}
                onSearch={() => {}}
                onFilterClick={() => {}}
                setLeadModalVisible={setLeadModalVisible}
                leadModalVisible={leadModalVisible}
                onRemoveFilter={() => {}}
                refetchWithFilters={setLeadsGuarantorsFiltars}
                filtersToShow={['agnet', 'brand', 'channel', 'city', 'created_at', 'lead_status', 'product_type', 'quantity', 'search_text', 'segment']}
            />

            <div className="border-2 border-black rounded-lg">
                <Table
                    columns={columns}
                    dataSource={leadsGurator?.items}
                    onChange={handleChange}
                    pagination={{
                        total: leadsGurator?.count,
                        pageSize: leadsGurator?.pageSize,
                        current: leadsGurator?.page,
                    }}
                    loading={isFetching}
                    rowKey={'id'}
                    size="small"
                    scroll={{ x: true }}
                />
            </div>
        </>
    );
};
